import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ThemeService } from '../../services/theme.service';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MenuButton } from '../../model/menu-button';
import { AuthService } from '../../../core/auth/auth.service';
import { PermissionService } from '../../../core/services/permission.service';
import { SystemPower } from '../../../shared/model/permissions';
import { OrganizationService } from '../../services/organization.service';

@Component({
  selector: 'app-header',
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
  standalone: true,
})
export class HeaderComponent implements OnInit {
  protected themeService: ThemeService = inject(ThemeService);
  protected authService: AuthService = inject(AuthService);
  protected permissionService: PermissionService = inject(PermissionService);
  protected organizationService: OrganizationService = inject(OrganizationService);
  protected canManageOrganizations = false;
  protected headerButtons: MenuButton[];
  protected navigationButtons: MenuButton[];
  readonly isMenuOpen = signal<boolean>(false);
  @Input() showTitle?: boolean = true;
  @Input() showThemeToggle?: boolean = true;
  @ViewChild('navbarContent') navbarContent!: ElementRef;

  constructor(private elementRef: ElementRef) {
    this.navigationButtons = [
      {
        label: 'Giochi',
        routerLink: '/games',
        showCondition: () => this.isAdmin(),
        elementId: 'gamesBtn',
      },
      { label: 'Eventi', routerLink: '/events', elementId: 'eventsBtn' },
      { label: 'Tavoli', routerLink: '/tables', elementId: 'tablesBtn' },
      { label: 'Organizzazioni', routerLink: '/organizations', elementId: 'organizationsBtn' },
      {
        label: 'Admin',
        routerLink: '/admin',
        showCondition: () => this.isAdmin(),
        elementId: 'adminBtn',
      },
    ];

    this.headerButtons = [
      {
        label: 'Login',
        routerLink: '/login',
        icon: 'bi bi-box-arrow-in-right',
        showCondition: () => !this.isLogged(),
        elementId: 'loginBtn',
      },
      {
        label: 'Profilo',
        routerLink: '/profile',
        icon: 'bi bi-person',
        showCondition: () => this.isLogged(),
        elementId: 'profileBtn',
      },
    ];
  }

  ngOnInit(): void {
    this.themeService.initTheme();
    this.initializeTooltips();
    this.checkOrganizationManagementAccess();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target as Node);
    if (!clickedInside) {
      this.closeMenu();
    }
  }

  toggleMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isMenuOpen.update((open) => !open);
  }

  onNavContentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) {
      this.closeMenu();
    }
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  private checkOrganizationManagementAccess(): void {
    if (!this.isLogged()) {
      this.canManageOrganizations = false;
      return;
    }
    if (this.isAdmin()) {
      this.canManageOrganizations = true;
      return;
    }
    const currentUser = this.permissionService.getCurrentUser();
    // Se il backend ha già esposto le organizzazioni nello user, verifica sincrona
    if (currentUser && Array.isArray(currentUser.organizations)) {
      this.canManageOrganizations = currentUser.organizations.some(
        (org) => org.admin || org.moderator || org.owner,
      );
      return;
    }
    // Fallback: backend non ancora aggiornato → verifica async
    this.organizationService.getAllOrganizations().subscribe({
      next: (response) => {
        this.canManageOrganizations =
          response.success && response.data
            ? response.data.some((org) => org.admin || org.moderator || org.owner)
            : false;
      },
      error: () => {
        this.canManageOrganizations = false;
      },
    });
  }

  isLogged(): boolean {
    return this.permissionService.isLogged();
  }

  isAdmin(): boolean {
    return this.permissionService.hasSystemPower(SystemPower.ADMINISTRATOR);
  }

  isAssociation(): boolean {
    return this.permissionService.hasSystemPower(SystemPower.ASSOCIATION);
  }

  getCurrentUserName(): string {
    const user = this.permissionService.getCurrentUser();
    return user?.userName || user?.email || 'Utente';
  }

  private initializeTooltips(): void {
    setTimeout(() => {
      const profileBtn = document.getElementById('profileBtn');
      if (profileBtn && 'Tooltip' in window) {
        try {
          new (window as any).bootstrap.Tooltip(profileBtn, {
            title: `Profilo: ${this.getCurrentUserName()}`,
            trigger: 'hover',
          });
        } catch (e) {
          console.debug('Bootstrap Tooltip non disponibile');
        }
      }
    }, 100);
  }
}
